/**
 * Normalize a hex color string to #RRGGBB uppercase.
 */
export const normalizeHex = (hex: string): string => {
    if (!hex) {
        return '#000000';
    }

    const trimmed = hex.trim();
    const value = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;

    if (value.length === 3) {
        const [r, g, b] = value;
        return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
    }

    if (value.length === 6) {
        return `#${value}`.toUpperCase();
    }

    return '#000000';
};

/**
 * Convert a hex color (#RGB or #RRGGBB) to RGB channels.
 */
export const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
    const normalized = normalizeHex(hex).slice(1);
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    return { r, g, b };
};

/**
 * Convert RGB channels to a hex color string.
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
    const clamp = (value: number) => Math.min(255, Math.max(0, Math.round(value)));
    const toHex = (value: number) => clamp(value).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
};

/**
 * Convert a hex color to HSL (h: 0-360, s: 0-100, l: 0-100).
 */
export const hexToHsl = (hex: string): { h: number; s: number; l: number } => {
    const { r, g, b } = hexToRgb(hex);
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;

    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    const delta = max - min;

    let h = 0;
    if (delta !== 0) {
        if (max === rNorm) {
            h = ((gNorm - bNorm) / delta) % 6;
        } else if (max === gNorm) {
            h = (bNorm - rNorm) / delta + 2;
        } else {
            h = (rNorm - gNorm) / delta + 4;
        }
    }

    h = Math.round(((h * 60) + 360) % 360);
    const l = (max + min) / 2;
    const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

    return {
        h,
        s: Math.round(s * 100),
        l: Math.round(l * 100),
    };
};

/**
 * Convert HSL (h: 0-360, s: 0-100, l: 0-100) to hex.
 */
export const hslToHex = (h: number, s: number, l: number): string => {
    const hue = ((h % 360) + 360) % 360;
    const saturation = Math.min(100, Math.max(0, s)) / 100;
    const lightness = Math.min(100, Math.max(0, l)) / 100;

    const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
    const huePrime = hue / 60;
    const x = chroma * (1 - Math.abs((huePrime % 2) - 1));

    let r1 = 0;
    let g1 = 0;
    let b1 = 0;

    if (huePrime >= 0 && huePrime < 1) {
        r1 = chroma;
        g1 = x;
    } else if (huePrime >= 1 && huePrime < 2) {
        r1 = x;
        g1 = chroma;
    } else if (huePrime >= 2 && huePrime < 3) {
        g1 = chroma;
        b1 = x;
    } else if (huePrime >= 3 && huePrime < 4) {
        g1 = x;
        b1 = chroma;
    } else if (huePrime >= 4 && huePrime < 5) {
        r1 = x;
        b1 = chroma;
    } else if (huePrime >= 5 && huePrime < 6) {
        r1 = chroma;
        b1 = x;
    }

    const m = lightness - chroma / 2;
    return rgbToHex((r1 + m) * 255, (g1 + m) * 255, (b1 + m) * 255);
};

/**
 * Calculate the relative luminance of a color per WCAG (0-1).
 */
export const getRelativeLuminance = (hex: string): number => {
    const { r, g, b } = hexToRgb(hex);
    const toLinear = (channel: number) => {
        const normalized = channel / 255;
        return normalized <= 0.03928
            ? normalized / 12.92
            : Math.pow((normalized + 0.055) / 1.055, 2.4);
    };

    const rLinear = toLinear(r);
    const gLinear = toLinear(g);
    const bLinear = toLinear(b);

    return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
};

/**
 * Calculate the WCAG contrast ratio between two colors.
 */
export const getContrastRatio = (foreground: string, background: string): number => {
    const lumA = getRelativeLuminance(foreground);
    const lumB = getRelativeLuminance(background);
    const [lighter, darker] = lumA >= lumB ? [lumA, lumB] : [lumB, lumA];
    return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Check WCAG AA/AAA contrast compliance for two colors.
 */
export const checkContrastWCAG = (
    fg: string,
    bg: string,
): { ratio: number; passAA: boolean; passAAA: boolean } => {
    const ratio = getContrastRatio(fg, bg);
    return {
        ratio,
        passAA: ratio >= 4.5,
        passAAA: ratio >= 7,
    };
};
